import { useState } from 'react';

import { AltynBankStatus } from '@/components/Shared/constants/banks-statuses';
import { Button } from '@/components/Shared/ui';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import InfoCircle from '@/media/svg/info-circle.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBank } from '@/store/financingStep.slice';
import { setCurrent } from '@/store/newOrder.slice';

import useSelectOffer from '../../../hooks/useSelectOffer';
import type { TBankOffers } from '../../../types/bank-offer.types';

const hideCancelButtonStatuses = [AltynBankStatus.Financed];

interface IProps {
  bankOffer: TBankOffers;
  setCancelModal?: (value: boolean) => void;
  setOfferDetailsModal: (value: boolean | string) => void;
}

const useHeaderActions = ({
  bankOffer,
  setCancelModal,
  setOfferDetailsModal
}: IProps) => {
  const dispatch = useAppDispatch();

  const { handleSelect } = useSelectOffer({ bankOffer });
  const {
    userAccesses: {
      autoCredit: { canAcceptOffer, canCancelOffer }
    }
  } = useAppSelector((state) => state.userInfo);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { selectedBank } = useAppSelector((state) => state.financingStep);

  const [showAlternatives, setShowAlternatives] = useState(false);
  const toggleAlternatives = () => setShowAlternatives(!showAlternatives);

  const canAccept =
    bankOffer.status.code === AltynBankStatus.Approved ||
    bankOffer.status.code === AltynBankStatus.AcceptedAlternative;

  // Блокируем кнопки "Принять" и "Отклонить", если заявка создана клиентом
  // и текущий bankOffer не имеет статуса "Принято клиентом"
  const isButtonsDisabled =
    (canAccept || bankOffer.status.code === AltynBankStatus.PreApproved) &&
    orderApp?.isOnlineRequest;

  const headerActions: React.ReactElement[] = [];
  if (
    bankOffer.isOffered &&
    bankOffer.status.code !== AltynBankStatus.Financed
  ) {
    headerActions.push(
      <Button
        key={bankOffer.id + 'continue'}
        size="small"
        variant="filled-red"
        onClick={() => {
          if (!selectedBank) dispatch(onSetSelectedBank(bankOffer));
          dispatch(setCurrent(2));
        }}
      >
        Продолжить
      </Button>
    );
  }
  if (canAccept || bankOffer.status.code === AltynBankStatus.ClientAccepted)
    headerActions.push(
      <Button
        key={bankOffer.id + 'accept'}
        size="small"
        variant="filled-red"
        onClick={() => handleSelect()}
        disabled={isButtonsDisabled || !canAcceptOffer}
      >
        Принять <span className="offer-body__hidden">предложение</span>
      </Button>
    );
  if (bankOffer.status.code === AltynBankStatus.PreApproved)
    headerActions.push(
      <Button
        key={bankOffer.id + 'accept-alternative'}
        size="small"
        variant="filled-red"
        onClick={() => handleSelect()}
        disabled={isButtonsDisabled}
      >
        Принять{' '}
        <span className="offer-body__hidden">предварительное одобрение</span>
      </Button>
    );
  if (
    !hideCancelButtonStatuses.includes(
      bankOffer.status.code as AltynBankStatus
    ) &&
    setCancelModal
  ) {
    headerActions.push(
      <Button
        key={bankOffer.id + 'reject'}
        size="small"
        onClick={() => setCancelModal(true)}
        disabled={isButtonsDisabled || !canCancelOffer}
      >
        Отклонить
      </Button>
    );
  }

  if (bankOffer.bankDescription)
    headerActions.push(
      <Button
        key={bankOffer.id + 'description'}
        onClick={() => setOfferDetailsModal(true)}
        size="small"
      >
        <InfoCircle />
        Описание
      </Button>
    );

  if (bankOffer.alternatives && bankOffer.alternatives.length > 0)
    headerActions.push(
      <Button
        className="offer-body__hidden"
        key={bankOffer.id + 'show-alternative'}
        size="small"
        onClick={toggleAlternatives}
      >
        Альтернативных решений: {bankOffer.alternatives?.length}
        <span
          // Стили для этих классов расположены в компоненте OfferCard
          className={`offer-header__chevron ${showAlternatives ? 'offer-header__chevron--active' : ''}`}
        >
          <ChevronDownIcon />
        </span>
      </Button>
    );
  return { headerActions, showAlternatives };
};

export default useHeaderActions;
